import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllTherapistAction,
  getTargetSignatureAccessAction,
  resetTargetSignatureAccess,
} from 'src/redux/actions/documentationAction';
import { checkSupervisorPermission } from 'src/utils/checkSignatureAccess';
import { REASONS } from './on-behalf-reason-modal';

/**
 * BRK-2074: the create-on-behalf dialog — pick the TARGET therapist the document will be authored
 * by, give the mandatory reason, and consent — shown before any on-behalf create runs the
 * existing create funnel (clock-in / payer / recert / discharge interstitials untouched).
 *
 * The target list is the facility's assigned therapists for the active discipline tab (the same
 * source the re-assign dialog uses; the backend ignores the role filter). Picking a target
 * fetches THAT provider's need-supervision maps ({t1, t2}, the BRK-2072 `providerId` param) into
 * their own reducer: t2 drives which templates the caller may create for the target, t1 later
 * decides the mint's isForSupervisor. Confirm fails closed until those maps resolve — and, on
 * the row-"+" path where the document type is already fixed, until the target's t2 allows it.
 */

interface SelectTargetTherapistProps {
  onClose: () => void;
  onConfirm: (target: { uuid: string; name: string }, reason: string) => void;
  activeTab: string;
  /**
   * Row-"+" path: the specific formType being created — confirm additionally requires the
   * target's t2 map to allow it. The Create-menu path passes nothing (the menu itself filters
   * by the target map).
   */
  formType?: string | null;
}

const SelectTargetTherapist: React.FC<SelectTargetTherapistProps> = ({
  onClose,
  onConfirm,
  activeTab,
  formType = null,
}) => {
  const dispatch = useDispatch();
  const therapists = useSelector((state: any) => state.getAllTherapistReducer);
  const targetAccess = useSelector((state: any) => state.getTargetSignatureAccessReducer);

  const [target, setTarget] = useState<any>(null);
  const [reason, setReason] = useState('');
  const [agree, setAgree] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    //  A previous compliance flow may have left maps behind — they belong to another target.
    dispatch(resetTargetSignatureAccess());
    dispatch(getAllTherapistAction(activeTab, 'therapist', null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickTarget = (_e: any, value: any) => {
    setTarget(value);
    //  The consent sentences name the picked target — a re-pick invalidates any affirmation
    //  given for the previous one (audit-trail integrity).
    setAgree(false);
    setConfirm(false);
    dispatch(resetTargetSignatureAccess());
    if (value?.uuid) {
      dispatch(
        getTargetSignatureAccessAction([], { discipline: activeTab, providerId: value.uuid })
      );
    }
  };

  const mapsReady = Boolean(targetAccess?.response?.data);
  const mapsFailed = Boolean(targetAccess?.isError);
  //  null = the menu path (no fixed type — the menu itself filters by the target map). Anything
  //  else, a missing/blank row formType included, must pass the target's t2 check — fail closed.
  const formTypeAllowed =
    formType === null || checkSupervisorPermission(targetAccess?.response?.data?.t2, formType);
  const isConfirmEnabled = Boolean(
    target && reason && agree && confirm && mapsReady && formTypeAllowed
  );

  const handleConfirm = () => {
    if (!isConfirmEnabled) return;
    onConfirm(target, reason);
  };

  return (
    <>
      <DialogTitle>Creating on behalf of</DialogTitle>
      <DialogContent>
        <Stack
          flexDirection={'column'}
          rowGap={1.5}
        >
          <Typography variant="body2">
            Select the therapist this documentation will be created on behalf of. The document is
            authored by the selected therapist; you are recorded as the acting user.
          </Typography>
          <Autocomplete
            getOptionLabel={(option: any) => option.name}
            fullWidth
            options={therapists.response?.data || []}
            onChange={handlePickTarget}
            value={target}
            disabled={therapists?.loading || therapists.response?.data?.length === 0}
            renderInput={(params): JSX.Element => (
              <TextField
                {...params}
                fullWidth
                label={
                  therapists.response?.data?.length === 0
                    ? 'No Records Found'
                    : 'Select Therapist Name'
                }
                name="targetTherapist"
              />
            )}
          />
          {target && mapsReady && !formTypeAllowed && (
            <FormHelperText error>
              This document type is not available for the selected therapist.
            </FormHelperText>
          )}
          {target && mapsFailed && (
            <FormHelperText error>
              Could not load the selected therapist&apos;s document settings. Re-select the
              therapist to try again.
            </FormHelperText>
          )}
          <FormControl
            fullWidth
            sx={{ mb: 2 }}
          >
            <InputLabel id="create-on-behalf-reason-label">Reason</InputLabel>
            <Select
              labelId="create-on-behalf-reason-label"
              value={reason}
              label="Reason"
              onChange={(e) => setReason(e.target.value)}
              renderValue={(selected) =>
                selected ? selected : <span style={{ color: '#6C737F' }}>Reason</span>
              }
            >
              {REASONS.map((r) => (
                <MenuItem
                  key={r}
                  value={r}
                >
                  <Radio
                    checked={reason === r}
                    value={r}
                    sx={{ padding: 0, marginRight: 1.5 }}
                  />
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I understand this action is recorded in the document&apos;s audit trail with my
                identity and the reason selected above.
              </Typography>
            }
            sx={{ alignItems: 'flex-start' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I confirm that I am creating documentation on behalf of{' '}
                <b>{target?.name || 'the selected therapist'}</b> and take full responsibility for
                this action.
              </Typography>
            }
            sx={{ alignItems: 'flex-start', mt: 1 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!isConfirmEnabled}
        >
          Continue
        </Button>
      </DialogActions>
    </>
  );
};

export default SelectTargetTherapist;
